/**
 * FIELD-001C · Pure URL builders / parsers for the Resource Management
 * ↔ Tasks ↔ Calendar navigation contract.
 *
 * Extracted from the render pipeline so the URL invariants can be
 * unit-tested without React + Router setup. Every rule Codex UAT
 * exercised in preview lives here in one place:
 *
 *   • Workload counters open outstanding work (`scope=pending`) and
 *     don't inherit unrelated remembered scopes.
 *   • Explicit scope / priority in `params` always win via spread.
 *   • Calendar URL is the shareable state — resource + view + date.
 *   • Local storage may fill defaults ONLY when the URL is silent.
 */
export const CALENDAR_UNASSIGNED_SENTINEL = "__unassigned__";
export const TASKS_UNASSIGNED_URL = "unassigned";

/**
 * Build a drill-down URL from a workload counter to the tasks module.
 *
 * `params` is the click-specific override — anything explicit
 * (`scope: "overdue"`, `priority: "Critical"`) wins via spread. The
 * default scope is `pending` because every counter on the workload
 * summary counts outstanding work.
 */
export function buildTaskDrillUrl(engineerId, extras = {}, { inOperations = true } = {}) {
    const merged = {
        scope: "pending",
        priority: "",
        engineer_id: engineerId,
        ...extras,
    };
    const clean = Object.fromEntries(
        Object.entries(merged).filter(([, v]) => v !== "" && v !== null && v !== undefined),
    );
    const qs = new URLSearchParams(clean).toString();
    const base = inOperations ? "/operations/tasks" : "/installer/tasks";
    return `${base}${qs ? `?${qs}` : ""}`;
}

/**
 * Compute the resolved initial `scope` for the Tasks module given
 * the current URL and localStorage. URL is authoritative:
 *   • Explicit `?scope=…` wins.
 *   • A drill-down (`?engineer_id` or `?priority`) with no scope
 *     resets to `pending` (outstanding work) — never the remembered
 *     Overdue / Completed from a previous visit.
 *   • Otherwise fall back to localStorage, then `all`.
 */
export function resolveInitialTasksScope(searchParams, storedScope) {
    const urlScope = searchParams.get("scope");
    if (urlScope) return urlScope;
    const hasDrill = !!searchParams.get("engineer_id") || !!searchParams.get("priority");
    if (hasDrill) return "pending";
    return storedScope || "all";
}

/**
 * Encode the resolved Tasks filters into a URLSearchParams instance
 * suitable for `setSearchParams`. Preserves unrelated params
 * verbatim; only touches the keys owned by this contract.
 *
 * VISIT-TASK-001D3 · Extended to include the free-text search
 * (`q`), the task category filter and the list/kanban view mode so
 * a browser Back after opening a canonical task restores the
 * user's originating list state (search + filters + view).
 */
export function encodeTasksUrl(currentParams, { scope, priority, status, project_id, engineer_id, q, task_category, view }) {
    const next = new URLSearchParams(currentParams);
    const set = (k, v) => { if (v) next.set(k, v); else next.delete(k); };
    set("scope", scope && scope !== "all" ? scope : null);
    set("priority", priority || null);
    set("status", status || null);
    set("project_id", project_id || null);
    set("q", (q || "").trim() || null);
    set("task_category", task_category || null);
    // View is only emitted when non-default so the URL stays clean
    // for the common list case.
    set("view", view === "kanban" ? "kanban" : null);
    if (engineer_id === CALENDAR_UNASSIGNED_SENTINEL) {
        next.set("engineer_id", TASKS_UNASSIGNED_URL);
    } else if (engineer_id) {
        next.set("engineer_id", engineer_id);
    } else {
        next.delete("engineer_id");
    }
    return next;
}

/**
 * Normalise an `engineer_id` URL value to the internal sentinel
 * used by the Tasks state machine. Both `unassigned` and the legacy
 * `__unassigned__` are accepted; anything else passes through.
 */
export function normaliseTaskEngineerParam(v) {
    return v === TASKS_UNASSIGNED_URL || v === CALENDAR_UNASSIGNED_SENTINEL
        ? CALENDAR_UNASSIGNED_SENTINEL
        : (v || "");
}

/**
 * Parse the incoming Calendar URL params into initial state values.
 * `date` follows YYYY-MM-DD (local calendar) — invalid values are
 * ignored rather than erroring.
 */
export function parseCalendarInitFromUrl(searchParams) {
    const engineerFilter = searchParams.get("engineer_filter");
    const focus = searchParams.get("focus");
    const dateStr = searchParams.get("date");
    let initialView = null;
    if (focus === "today") initialView = "day";
    else if (focus === "week") initialView = "week";
    else if (focus === "month") initialView = "month";
    let initialDate = null;
    if (dateStr && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const parsed = new Date(`${dateStr}T12:00:00`);
        if (!Number.isNaN(parsed.getTime())) initialDate = parsed;
    }
    return { engineerFilter, initialView, initialDate };
}

/**
 * Encode the resolved Calendar state into a URLSearchParams instance
 * for `setSearchParams`. Emits the trio `engineer_filter`, `focus`,
 * `date` so refreshes / copied links reproduce the same view.
 */
export function encodeCalendarUrl(currentParams, { engineerId, assigned, viewKey, dateIso }) {
    const next = new URLSearchParams(currentParams);
    const set = (k, v) => { if (v) next.set(k, v); else next.delete(k); };
    if (assigned === "false") set("engineer_filter", CALENDAR_UNASSIGNED_SENTINEL);
    else if (engineerId) set("engineer_filter", engineerId);
    else set("engineer_filter", null);
    set("focus", viewKey === "day" ? "today" : viewKey === "week" ? "week" : viewKey === "month" ? "month" : null);
    set("date", dateIso || null);
    return next;
}
